<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-container large-modal" @click.stop>
      <div class="modal-header">
        <div class="header-content">
          <h3><i class="fas fa-user-cog"></i> Donner Produits au Technicien</h3>
          <p>Sélectionnez le technicien et les produits à lui donner</p>
        </div>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <!-- Sélection Technicien avec Recherche -->
        <div class="form-group">
          <label class="required">Technicien</label>
          <div class="technicien-search-wrapper" :class="{ 'is-open': showTechnicienDropdown }">
            <div class="search-input-container" @click="showTechnicienDropdown = true">
              <i class="fas fa-user-cog search-icon"></i>
              <input 
                v-model="technicienSearchQuery" 
                @input="showTechnicienDropdown = true"
                @focus="showTechnicienDropdown = true"
                placeholder="Rechercher un technicien..."
                class="form-control technicien-search-input"
              >
              <i class="fas fa-chevron-down dropdown-icon" :class="{ 'rotated': showTechnicienDropdown }"></i>
            </div>
            
            <!-- Dropdown des techniciens -->
            <div v-if="showTechnicienDropdown" class="technicien-dropdown">
              <div 
                v-for="tech in filteredTechniciens" 
                :key="tech._id"
                @click="selectTechnicien(tech)"
                class="technicien-option"
                :class="{ 'selected': selectedTechnicien === tech._id }"
              >
                <div class="tech-avatar">
                  <i class="fas fa-user"></i>
                </div>
                <div class="tech-info">
                  <div class="tech-name">{{ tech.prenom }} {{ tech.nom }}</div>
                  <div class="tech-email" v-if="tech.email">{{ tech.email }}</div>
                </div>
                <i v-if="selectedTechnicien === tech._id" class="fas fa-check check-icon"></i>
              </div>
              
              <div v-if="filteredTechniciens.length === 0" class="no-results">
                <i class="fas fa-search"></i>
                <p>Aucun technicien trouvé</p>
              </div>
            </div>
          </div>
          
          <!-- Technicien sélectionné -->
          <div v-if="selectedTechnicienData" class="selected-technicien">
            <div class="selected-tech-avatar">
              <i class="fas fa-user"></i>
            </div>
            <div class="selected-tech-info">
              <strong>{{ selectedTechnicienData.prenom }} {{ selectedTechnicienData.nom }}</strong>
              <span v-if="selectedTechnicienData.email">{{ selectedTechnicienData.email }}</span>
            </div>
            <button @click="clearTechnicien" class="clear-btn" type="button">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Recherche Produits -->
        <div class="search-section">
          <div class="search-wrapper">
            <i class="fas fa-search"></i>
            <input 
              v-model="searchQuery" 
              placeholder="Rechercher par nom, référence, catégorie, IMEI ou ICCID..." 
              class="search-input"
            >
          </div>
        </div>

        <!-- Liste Produits Disponibles -->
        <div class="products-section">
          <h4><i class="fas fa-boxes"></i> Produits Disponibles ({{ availableProducts.length }})</h4>
          
          <div v-if="loading" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Chargement des produits...</p>
          </div>
          
          <div v-else-if="filteredProducts.length === 0" class="empty-state">
            <i class="fas fa-inbox"></i>
            <p>Aucun produit disponible</p>
          </div>
          
          <div v-else class="products-list">
            <div 
              v-for="product in filteredProducts" 
              :key="product._id"
              class="product-item"
              @click="addProduct(product)"
            >
              <div class="product-info">
                <h5>{{ product.designation }}</h5>
                <!-- IMEI ou ICCID en grand -->
                <div v-if="product.imei || product.iccid" class="product-identifier-large">
                  <span v-if="product.imei" class="identifier-badge imei">
                    <i class="fas fa-barcode"></i> IMEI: {{ product.imei }}
                  </span>
                  <span v-if="product.iccid" class="identifier-badge iccid">
                    <i class="fas fa-sim-card"></i> ICCID: {{ product.iccid }}
                  </span>
                </div>
                <div class="product-meta">
                  <span class="product-category">{{ product.categorie }}</span>
                </div>
              </div>
              <div class="product-stock">
                <i class="fas fa-cubes"></i>
                <span>Qté: {{ product.quantite }}</span>
              </div>
              <button class="btn-add">
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Produits Sélectionnés -->
        <div class="selected-section" v-if="selectedProducts.length > 0">
          <h4><i class="fas fa-check-circle"></i> Produits Sélectionnés ({{ selectedProducts.length }})</h4>
          <div class="selected-list">
            <div 
              v-for="(item, index) in selectedProducts" 
              :key="index"
              class="selected-item"
            >
              <div class="item-info">
                <h5>{{ item.product.designation }}</h5>
                <!-- IMEI ou ICCID au lieu de la référence -->
                <p v-if="item.product.imei" class="selected-identifier">
                  <i class="fas fa-barcode"></i> IMEI: {{ item.product.imei }}
                </p>
                <p v-else-if="item.product.iccid" class="selected-identifier">
                  <i class="fas fa-sim-card"></i> ICCID: {{ item.product.iccid }}
                </p>
                <span class="selected-category">{{ item.product.categorie }}</span>
              </div>
              <div class="item-quantity">
                <button @click="decreaseQuantity(index)" class="btn-qty">
                  <i class="fas fa-minus"></i>
                </button>
                <input 
                  v-model.number="item.quantite" 
                  type="number" 
                  min="1" 
                  :max="item.product.quantite"
                  class="qty-input"
                >
                <button @click="increaseQuantity(index)" class="btn-qty">
                  <i class="fas fa-plus"></i>
                </button>
              </div>
              <button @click="removeProduct(index)" class="btn-remove">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-cancel">
          <i class="fas fa-times"></i> Annuler
        </button>
        <button 
          @click="handleSubmit" 
          class="btn-submit"
          :disabled="!canSubmit || submitting"
        >
          <i class="fas" :class="submitting ? 'fa-spinner fa-spin' : 'fa-check'"></i>
          {{ submitting ? 'Traitement...' : 'Donner les Produits' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import stockService from '@/services/stockService'
import userService from '@/services/userService'

const store = useStore()
const emit = defineEmits(['close', 'success'])

const techniciens = ref([])
const selectedTechnicien = ref('')
const selectedTechnicienData = ref(null)
const technicienSearchQuery = ref('')
const showTechnicienDropdown = ref(false)
const availableProducts = ref([])
const selectedProducts = ref([])
const searchQuery = ref('')
const submitting = ref(false)
const loading = ref(true)

const filteredTechniciens = computed(() => {
  if (!technicienSearchQuery.value) return techniciens.value
  const query = technicienSearchQuery.value.toLowerCase()
  return techniciens.value.filter(tech => 
    tech.prenom.toLowerCase().includes(query) ||
    tech.nom.toLowerCase().includes(query) ||
    (tech.email && tech.email.toLowerCase().includes(query))
  )
})

const filteredProducts = computed(() => {
  if (!searchQuery.value) return availableProducts.value
  const query = searchQuery.value.toLowerCase()
  return availableProducts.value.filter(p => 
    p.designation.toLowerCase().includes(query) ||
    p.referenceInterne.toLowerCase().includes(query) ||
    (p.categorie && p.categorie.toLowerCase().includes(query)) ||
    (p.imei && p.imei.toLowerCase().includes(query)) ||
    (p.iccid && p.iccid.toLowerCase().includes(query))
  )
})

const selectTechnicien = (tech) => {
  selectedTechnicien.value = tech._id
  selectedTechnicienData.value = tech
  technicienSearchQuery.value = `${tech.prenom} ${tech.nom}`
  showTechnicienDropdown.value = false
}

const clearTechnicien = () => {
  selectedTechnicien.value = ''
  selectedTechnicienData.value = null
  technicienSearchQuery.value = ''
}

const canSubmit = computed(() => {
  return selectedTechnicien.value && selectedProducts.value.length > 0
})

const loadTechniciens = async () => {
  try {
    const response = await userService.getUsers({ role: 'Technicien' })
    // Gérer différents formats de réponse (utilisateurs, users, data)
    techniciens.value = response.utilisateurs || response.data?.utilisateurs || response.users || response.data?.users || response.data || []
    console.log('Techniciens chargés:', techniciens.value.length)
  } catch (error) {
    console.error('Erreur chargement techniciens:', error)
    store.dispatch('notifications/addNotification', {
      type: 'error',
      message: 'Erreur lors du chargement des techniciens'
    })
  }
}

const loadAvailableProducts = async () => {
  try {
    const response = await stockService.getProducts({ statut: 'en_stock' })
    // Gérer différents formats de réponse
    availableProducts.value = response.data?.products || response.products || response.data || []
    console.log('Produits chargés:', availableProducts.value.length)
  } catch (error) {
    console.error('Erreur chargement produits:', error)
    store.dispatch('notifications/addNotification', {
      type: 'error',
      message: 'Erreur lors du chargement des produits'
    })
  }
}

const addProduct = (product) => {
  // Vérifier si déjà ajouté
  const exists = selectedProducts.value.find(item => item.product._id === product._id)
  if (exists) {
    store.dispatch('notifications/addNotification', {
      type: 'warning',
      message: 'Ce produit est déjà dans la liste'
    })
    return
  }

  selectedProducts.value.push({
    product: product,
    quantite: 1
  })
}

const removeProduct = (index) => {
  selectedProducts.value.splice(index, 1)
}

const increaseQuantity = (index) => {
  const item = selectedProducts.value[index]
  if (item.quantite < item.product.quantite) {
    item.quantite++
  }
}

const decreaseQuantity = (index) => {
  const item = selectedProducts.value[index]
  if (item.quantite > 1) {
    item.quantite--
  }
}

const handleSubmit = async () => {
  if (!canSubmit.value) return

  submitting.value = true
  try {
    // Créer les mouvements de stock pour chaque produit
    for (const item of selectedProducts.value) {
      await stockService.createMovement({
        typeMouvement: 'sortie_technicien',
        produit: item.product._id,
        quantite: item.quantite,
        emplacementOrigine: 'magasin_central',
        emplacementDestination: `sac_technicien_${selectedTechnicien.value}`,
        motif: `Produits donnés au technicien`,
        technicien: selectedTechnicien.value
      })
    }

    store.dispatch('notifications/addNotification', {
      type: 'success',
      message: `${selectedProducts.value.length} produit(s) donné(s) au technicien avec succès`
    })

    emit('success')
    emit('close')
  } catch (error) {
    console.error('Erreur:', error)
    store.dispatch('notifications/addNotification', {
      type: 'error',
      message: 'Erreur lors du transfert des produits'
    })
  } finally {
    submitting.value = false
  }
}

// Fermer le dropdown quand on clique en dehors
const handleClickOutside = (event) => {
  const wrapper = event.target.closest('.technicien-search-wrapper')
  if (!wrapper && showTechnicienDropdown.value) {
    showTechnicienDropdown.value = false
  }
}

onMounted(async () => {
  loading.value = true
  await Promise.all([
    loadTechniciens(),
    loadAvailableProducts()
  ])
  loading.value = false
  
  // Ajouter l'écouteur pour fermer le dropdown
  document.addEventListener('click', handleClickOutside)
})

// Nettoyer l'écouteur au démontage
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  border-radius: 16px 16px 0 0;
}

.header-content h3 {
  margin: 0 0 8px 0;
  font-size: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-content p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.3s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
}

.form-group label.required::after {
  content: ' *';
  color: #ef4444;
}

.form-control {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Recherche Technicien */
.technicien-search-wrapper {
  position: relative;
}

.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.search-icon {
  position: absolute;
  left: 16px;
  color: #3b82f6;
  z-index: 1;
}

.technicien-search-input {
  padding-left: 48px !important;
  padding-right: 40px !important;
}

.dropdown-icon {
  position: absolute;
  right: 16px;
  color: #9ca3af;
  transition: transform 0.3s ease;
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
}

.technicien-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.technicien-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s;
  border-bottom: 1px solid #f3f4f6;
}

.technicien-option:last-child {
  border-bottom: none;
}

.technicien-option:hover {
  background: #eff6ff;
}

.technicien-option.selected {
  background: #dbeafe;
}

.tech-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.tech-info {
  flex: 1;
}

.tech-name {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.tech-email {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.check-icon {
  color: #10b981;
  font-size: 16px;
}

.no-results {
  text-align: center;
  padding: 24px;
  color: #9ca3af;
}

.no-results i {
  font-size: 24px;
  margin-bottom: 8px;
  display: block;
}

.no-results p {
  margin: 0;
  font-size: 14px;
}

/* Technicien sélectionné */
.selected-technicien {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%);
  border: 2px solid #3b82f6;
  border-radius: 8px;
  margin-top: 12px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.selected-tech-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  flex-shrink: 0;
}

.selected-tech-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.selected-tech-info strong {
  color: #1e40af;
  font-size: 14px;
}

.selected-tech-info span {
  color: #6b7280;
  font-size: 12px;
}

.clear-btn {
  background: rgba(239, 68, 68, 0.1);
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ef4444;
  transition: all 0.3s;
  flex-shrink: 0;
}

.clear-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.1);
}

.search-section {
  margin-bottom: 24px;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-wrapper i {
  position: absolute;
  left: 16px;
  color: #9ca3af;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.products-section h4,
.selected-section h4 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.products-list {
  display: grid;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px;
}

.product-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.product-item:hover {
  border-color: #3b82f6;
  background: #eff6ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.product-info {
  flex: 1;
}

.product-info h5 {
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #111827;
}

.product-ref {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.product-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.product-category {
  display: inline-block;
  padding: 2px 8px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.product-identifier {
  display: inline-block;
  padding: 2px 8px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

/* IMEI/ICCID en grand */
.product-identifier-large {
  margin: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.identifier-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Courier New', Courier, monospace;
  letter-spacing: 0.5px;
}

.identifier-badge i {
  font-size: 16px;
}

.identifier-badge.imei {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border: 2px solid #f59e0b;
}

.identifier-badge.iccid {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border: 2px solid #3b82f6;
}

.product-stock {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #059669;
  font-weight: 600;
}

.btn-add {
  background: #3b82f6;
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.btn-add:hover {
  background: #2563eb;
  transform: scale(1.1);
}

.selected-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px dashed #e5e7eb;
}

.selected-list {
  display: grid;
  gap: 12px;
}

.selected-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
}

.item-info {
  flex: 1;
}

.item-info h5 {
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #111827;
}

.item-info p {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.selected-identifier {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Courier New', Courier, monospace;
  font-weight: 600;
  font-size: 13px;
  color: #92400e;
  margin: 4px 0;
}

.selected-identifier i {
  color: #f59e0b;
}

.selected-category {
  display: inline-block;
  padding: 2px 8px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-top: 4px;
}

.item-quantity {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-qty {
  background: #e5e7eb;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.btn-qty:hover {
  background: #d1d5db;
}

.qty-input {
  width: 60px;
  padding: 6px;
  border: 2px solid #e5e7eb;
  border-radius: 4px;
  text-align: center;
  font-weight: 600;
}

.btn-remove {
  background: #fee2e2;
  color: #dc2626;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.btn-remove:hover {
  background: #fecaca;
  transform: scale(1.1);
}

.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-cancel,
.btn-submit {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
}

.btn-cancel {
  background: #f3f4f6;
  color: #374151;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

.btn-submit {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.loading-state i,
.empty-state i {
  font-size: 32px;
  margin-bottom: 12px;
  display: block;
}

.loading-state i {
  color: #3b82f6;
}

/* ========== RESPONSIVE MOBILE ========== */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .modal-container {
    width: 100%;
    max-width: 100%;
    max-height: 95vh;
    border-radius: 16px 16px 0 0;
    margin: 0;
  }

  .modal-header {
    padding: 16px;
    border-radius: 16px 16px 0 0;
  }

  .header-content h3 {
    font-size: 18px;
    gap: 8px;
  }

  .header-content p {
    font-size: 12px;
  }

  .modal-body {
    padding: 16px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .technicien-dropdown {
    max-height: 200px;
  }

  .technicien-option {
    padding: 10px 12px;
  }

  .tech-avatar {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .tech-name {
    font-size: 13px;
  }

  .tech-email {
    font-size: 11px;
  }

  .selected-technicien {
    padding: 10px;
    gap: 10px;
  }

  .selected-tech-avatar {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .search-section {
    margin-bottom: 16px;
  }

  .products-section h4,
  .selected-section h4 {
    font-size: 14px;
    margin-bottom: 12px;
  }

  .products-list {
    max-height: 200px;
  }

  .product-item {
    padding: 12px;
    gap: 10px;
    flex-wrap: wrap;
  }

  .product-info {
    flex: 1 1 100%;
    min-width: 0;
  }

  .product-info h5 {
    font-size: 13px;
  }

  .product-identifier-large {
    margin: 6px 0;
  }

  .identifier-badge {
    padding: 6px 10px;
    font-size: 12px;
  }

  .identifier-badge i {
    font-size: 14px;
  }

  .product-stock {
    font-size: 12px;
  }

  .btn-add {
    width: 28px;
    height: 28px;
  }

  .selected-section {
    margin-top: 16px;
    padding-top: 16px;
  }

  .selected-item {
    padding: 12px;
    gap: 10px;
    flex-wrap: wrap;
  }

  .item-info {
    flex: 1 1 100%;
    min-width: 0;
  }

  .item-info h5 {
    font-size: 13px;
  }

  .selected-identifier {
    font-size: 12px;
  }

  .item-quantity {
    gap: 6px;
  }

  .btn-qty {
    width: 24px;
    height: 24px;
  }

  .qty-input {
    width: 50px;
    padding: 4px;
    font-size: 13px;
  }

  .btn-remove {
    width: 28px;
    height: 28px;
  }

  .modal-footer {
    padding: 16px;
    flex-direction: column;
    gap: 10px;
  }

  .btn-cancel,
  .btn-submit {
    width: 100%;
    justify-content: center;
    padding: 14px 20px;
  }
}

@media (max-width: 480px) {
  .modal-container {
    max-height: 100vh;
    border-radius: 0;
  }

  .modal-header {
    padding: 12px;
    border-radius: 0;
  }

  .header-content h3 {
    font-size: 16px;
  }

  .modal-body {
    padding: 12px;
  }

  .products-list {
    max-height: 150px;
  }

  .product-item {
    padding: 10px;
  }

  .identifier-badge {
    padding: 4px 8px;
    font-size: 11px;
  }

  .selected-item {
    padding: 10px;
  }

  .modal-footer {
    padding: 12px;
  }
}
</style>
